<template>
  <div>
    <b-tabs class="d-flex flex-column" fill  active-nav-item-class="font-weight-bold text-uppercase">
      <b-tab v-if="isCustomer && checkClaim(CAN_VIEW_ALL_REQUIREMENTS)">
        <template v-slot:title>
          Requirements &nbsp;<b-badge variant="primary">{{ companyRecordsCount.reqCounts }}</b-badge>
        </template>
        <keep-alive>
          <requirement-lines :companyId="companyId" />  
        </keep-alive>        
      </b-tab>
      <b-tab v-if="checkClaim(CAN_VIEW_ALL_QUOTE) && isCustomer">
        <template v-slot:title>
          Quotes &nbsp;<b-badge variant="primary">{{ companyRecordsCount.quoteCounts }}</b-badge>
        </template>
        <keep-alive>
          <quote-lines :companyId="companyId" />  
        </keep-alive>
      </b-tab>
      <b-tab v-if="isCustomer && checkClaim(CAN_VIEW_ALL_SALES_ORDERS)">
        <template v-slot:title>
          Sales Orders &nbsp;<b-badge variant="primary">{{ companyRecordsCount.soCounts }}</b-badge>
        </template>
        <keep-alive>
          <sales-order-lines :companyId="companyId" />  
        </keep-alive>        
      </b-tab>
      <b-tab v-if="isCustomer && checkClaim(CAN_VIEW_ALL_INVOICES)">
        <template v-slot:title>
          Invoices &nbsp;<b-badge variant="primary">{{ companyRecordsCount.invoiceCounts }}</b-badge>
        </template>
        <keep-alive>
          <invoice-lines :companyId="companyId" />  
        </keep-alive>        
      </b-tab>
      <b-tab v-if="isCustomer && checkClaim(CAN_VIEW_ALL_RMAS)">
        <template v-slot:title>
          RMAs &nbsp;<b-badge variant="primary">{{ companyRecordsCount.rmaCounts }}</b-badge>
        </template>
        <keep-alive>
          <rma-lines :companyId="companyId" />
        </keep-alive>        
      </b-tab>
      <b-tab v-if="isCustomer && checkClaim(CAN_VIEW_CREDIT_MEMO)">
        <template v-slot:title>
          Credit Memo &nbsp;<b-badge variant="success">{{ companyRecordsCount.creditMemoBalance }}</b-badge>
        </template>
        <keep-alive>
          <credit-memos :companyId="companyId" :isPending="false" :hasBalance="true" />
        </keep-alive>        
      </b-tab>
      <b-tab v-if="isVendor && checkClaim(CAN_VIEW_ALL_OFFERS)">
        <template v-slot:title>
          Offers &nbsp;<b-badge variant="primary">{{ companyRecordsCount.offerCounts }}</b-badge>
        </template>
        <keep-alive>
          <offers :companyId="companyId" :gridId="offersGrid" />
        </keep-alive>        
      </b-tab>
      <b-tab v-if="isVendor && checkClaim(CAN_VIEW_ALL_POS)">
        <template v-slot:title>
          Purchase Orders &nbsp;<b-badge variant="primary">{{ companyRecordsCount.poCounts }}</b-badge>
        </template>
        <keep-alive>
          <purchase-order-lines :companyId="companyId" />
        </keep-alive>        
      </b-tab>
      <b-tab v-if="isVendor && checkClaim(CAN_VIEW_ALL_WORK_ORDERS) && isTestHouse">
        <template v-slot:title>
          Work Orders &nbsp;<b-badge variant="primary">{{ companyRecordsCount.woCounts }}</b-badge>
        </template>
        <keep-alive>
          <work-order-lines :companyId="companyId" />
        </keep-alive>        
      </b-tab>
      <b-tab v-if="isVendor && checkClaim(CAN_VIEW_ALL_VENDOR_RETURNS)">
        <template v-slot:title>
          Vendor RMA &nbsp;<b-badge variant="primary">{{ companyRecordsCount.vendorRMACounts }}</b-badge>
        </template>
        <keep-alive>
          <vendor-rma-lines :companyId="companyId" />
        </keep-alive>
      </b-tab>
      <b-tab>
        <template v-slot:title>
          Todo Task &nbsp;<b-badge variant="primary">{{ companyRecordsCount.taskCounts }}</b-badge>
        </template>
        <keep-alive>
          <todo-tasks :companyId="companyId" :companyName="companyName" />  
        </keep-alive>        
      </b-tab>
      <b-tab v-if="checkClaim(CAN_VIEW_PARTS)">
        <template v-slot:title>
          Customer/Vendor Parts &nbsp;<b-badge variant="primary">{{ companyRecordsCount.companyPartsCounts }}</b-badge>
        </template>
        <keep-alive>
          <company-parts :companyId="companyId" :companyInfo="{id:companyId, name:companyName, class: companyClass}" />  
        </keep-alive>        
      </b-tab>
      <b-tab title="Consignments">
        <template v-slot:title>
          Consignments &nbsp;<b-badge variant="primary">{{ companyRecordsCount.consignmentCounts }}</b-badge>
        </template>
        <keep-alive>
          <consignments :partId="null" :companyId="companyId" :companyInfo="{id:companyId, name:companyName}"/>  
        </keep-alive>        
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import RequirementLines from '@/shared/components/RequirementLines';
import QuoteLines from '@/shared/components/QuoteLines';
import SalesOrderLines from '@/shared/components/SalesOrderLines';
import InvoiceLines from '@/shared/components/InvoiceLines-new';
import RmaLines from '@/shared/components/RmaLines';
import Offers from '@/shared/components/Offers';
import PurchaseOrderLines from '@/shared/components/PurchaseOrderLines-new';
import WorkOrderLines from '@/shared/components/ServiceOrderLines-new';
import VendorRmaLines from '@/shared/components/VendorRmaLines';
import CreditMemos from '@/shared/components/CreditMemo-new';
import TodoTasks from '@/shared/components/TodoTasks';
import CompanyParts from '@/shared/components/CompanyParts';
import Consignments from '@/shared/components/Consignments';
import { GRID_ID } from '@/shared/constants';
import { mapGetters, mapActions } from 'vuex';

export default {
  components: {
    RequirementLines,
    QuoteLines,
    SalesOrderLines,
    InvoiceLines,
    RmaLines,
    Offers,
    PurchaseOrderLines,
    WorkOrderLines,
    VendorRmaLines,
    CreditMemos,
    TodoTasks,
    CompanyParts,
    Consignments
  },
  props: {
    companyId: {
      type: [String, Number],
      default: 0,
    },
    companyName: {
      type: [String],
      default: '',
    },
    isVendor: {
      type: Boolean,
      default: false,
    },
    isCustomer: {
      type: Boolean,
      default: false,
    },
    isTestHouse: {
      type: Boolean,
      default: false
    },
    companyClass: {
      type: Number,
      default: 0
    }
  },
  computed: {
    ...mapGetters('requirements', ['filteredRequirement']),
    // ...mapGetters('quotes', ['quotesFiltered']),
    ...mapGetters('salesOrders', ['salesOrdersFiltered']),
    ...mapGetters('invoices', ['invoices']),
    ...mapGetters('customerRmasLines', ['rmaLinesByFilter']),
    ...mapGetters('offers', ['offersList']),
    ...mapGetters('purchaseorders', ['purchaseOrdersFiltered']),
    ...mapGetters('vendorrmas', ['vrmasListByCompany']),
    ...mapGetters('todotasks', ['gridList']),
    ...mapGetters('companyParts', { companyParts: 'gridList'}),
    ...mapGetters('consignments', { consignments: 'gridList'}),
    ...mapGetters('companies', ['companyRecordsCount'])
  },
  data() {
    return {
      offersGrid: GRID_ID.COMPANY_DETAILS_OFFERS
    }
  },
  methods: {
    ...mapActions('companies', ['getCompanyRecordsCount']),
  },
  created() {
    this.getCompanyRecordsCount(this.companyId);
  },

};
</script>
